import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import Question from '../FAQs/Question';
import type { FAQDoc } from '../../../../../general/Input';
import Translation from '../../../../../../general/Translation/Translation';
import type {
  BankDetail,
  CustomerService,
  Category as CategoryType,
} from '../../../../types';
import { useInViewContext } from '../../../../Context/InViewContextProvider';
import slugify from '../../../../../../../js/util/slugify';
import ButtonStack from '../../../../../../general/Button/ButtonStack';
import Button from '../../../../../../general/Button/Button';

interface CategoryProps {
  bankDetail: BankDetail;
  category: CategoryType;
  contactInfo: CustomerService;
  faqUri: string;
  faqs: FAQDoc[];
  isSearched: boolean;
  selectedQuestionId: string;
}

const Category: React.FC<CategoryProps> = ({
  bankDetail,
  category: { id, name },
  contactInfo,
  faqUri,
  faqs,
  isSearched,
  selectedQuestionId,
}) => {
  const showAmount = 5;
  const { inView, ref } = useInView();
  const [, setInView] = useInViewContext();

  useEffect(() => {
    setInView((current) => ({
      ...current,
      [id]: inView,
    }));
  }, [id, inView, setInView]);

  const [showAllClick, setShowAllClick] = useState<boolean>(false);
  const [showAllSearch, setShowAllSearch] = useState<boolean>(false);

  const hasSelectedQuestion = !!faqs.find(
    (f) => f.objectID === selectedQuestionId,
  );

  useEffect(() => {
    if (selectedQuestionId && hasSelectedQuestion) {
      setShowAllClick(true);
    }
  }, [selectedQuestionId, hasSelectedQuestion]);

  useEffect(() => {
    setShowAllSearch(isSearched);
  }, [showAllSearch, isSearched]);

  const showMore = () => {
    setShowAllClick(true);
  };
  const showLess = () => {
    setShowAllClick(false);
  };

  const ShowMoreLessButton = () => {
    if (isSearched || faqs.length <= 5) {
      return null;
    }

    if (showAllClick) {
      return (
        <ButtonStack isSm>
          <Button
            isGhost
            onClick={showLess}
            aria-label="show-less"
          >
            <Translation
              pageString="productinformatie"
              stringId="read_less"
            />
          </Button>
        </ButtonStack>
      );
    }
    return (
      <ButtonStack isSm>
        <Button
          isGhost
          onClick={showMore}
          aria-label="show-more"
        >
          <Translation
            pageString="overview_page"
            stringId="show_more"
          />
        </Button>
      </ButtonStack>
    );
  };

  return (
    <div
      id={slugify(name)}
      className="block block--boxed block--border block--fluid"
    >
      <div className="content content--fluid">
        <h3
          className="h4"
          ref={ref}
        >
          {name}
        </h3>
        {faqs.map((faq, i) => (
          <React.Fragment key={faq.question}>
            {(showAllClick || showAllSearch || showAmount > i) && (
              <Question
                faq={faq}
                selectedQuestionId={selectedQuestionId}
                faqUri={faqUri}
                bankDetail={bankDetail}
                contactInfo={contactInfo}
              />
            )}
          </React.Fragment>
        ))}

        <ShowMoreLessButton />
      </div>
    </div>
  );
};

export default Category;
